import { ArrowForwardRounded } from '@material-ui/icons';
import { useStyles } from '@webapps/styles';
import {
  generateContract,
  downloadCv,
  getProfilePresentation,
  getConventionByRecord,
} from '@webapps/business';
import { useAuth, useMutation, useQuery, useToast } from '@webapps/reactjs-tech-components';
import { useEffect, useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CircularProgress } from '@material-ui/core';
import Header from 'pages/CFA/Convention/components/Header';
import Footer from 'pages/CFA/Convention/components/Footer';
import { renderToString } from 'react-dom/server';

function transformDocument(input) {
  if (!input) return null;
  if (!input?.content) return null;

  const parsedContent = JSON.parse(input?.content); // le content est une string JSON

  const concatenatedText = parsedContent.map(({ text }) => text).join(' ');

  return {
    id: input?.id,
    name: input?.name,
    text: concatenatedText,
    hasUnsavedChanges: false,
  };
}

const generateCompleteHTML = (contentHtml) => {
  if (!contentHtml) return null;

  const completeHTML = `
  <!DOCTYPE html>
  <html lang="fr">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Convention de Formation</title>
    <style>
      body {
        font-family: Roboto, Arial, sans-serif;
        line-height: 1.5;
        color: #333;
        margin: 0;
        padding: 0;
        font-size: 11px;
      }
      .page-content {
        width: 210mm; /* A4 width */
        min-height: 297mm; /* A4 height */
        box-sizing: border-box;
        padding: 20mm;
        margin: 0 auto;
        position: relative;
      }
      h1, h2, h3 {
        color: #1472ff;
        margin-top: 20px;
        margin-bottom: 15px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 15px 0;
      }
      th, td {
        border: 1px solid #ccc;
        padding: 8px;
        text-align: center;
        font-size: 11px;
        padding: 1px;
      }
      th {
        background-color: #f5f5f5;
      }
      #footer {
        margin-top: 20px;
        position: relative;
      }
    </style>
  </head>
  <body>
    ${contentHtml}
  </body>
  </html>
  `.trim();

  return completeHTML;
}


/**
 * Remplit les smartfields dans les pages HTML d'un document.
 * @param {object} document - Objet avec structure { id, name, pages, hasUnsavedChanges }
 * @param {object} values - Objet clé => valeur pour chaque smartfield (ex : { cfaName: 'EPITECH' })
 * @returns {object} - Nouveau document avec les textes mis à jour
 */
function fillSmartfields(htmlContent, values) {
  if (!htmlContent) return htmlContent;

  const tableStartIndexes = [];
  const tableEndIndexes = [];
  const footerStartIndex = htmlContent.indexOf('<div id="footer"');
  let footerEndIndex = -1;

  // Find the correct footer end index by looking for the last </div> within the footer context
  if (footerStartIndex !== -1) {
    let openDivs = 1;
    let currentIndex = footerStartIndex + 12; // Move past <div id="footer"
    while (currentIndex < htmlContent.length && openDivs > 0) {
      const nextDivStart = htmlContent.indexOf('<div', currentIndex);
      const nextDivEnd = htmlContent.indexOf('</div>', currentIndex);
      if (nextDivEnd === -1) break;
      if (nextDivStart !== -1 && nextDivStart < nextDivEnd) {
        openDivs++;
        currentIndex = nextDivStart + 4;
      } else {
        openDivs--;
        currentIndex = nextDivEnd + 6;
        if (openDivs === 0) {
          footerEndIndex = nextDivEnd + 6;
        }
      }
    }
  }

  const tableIds = ['financialTable', 'spendingTable'];

  // Find all table ranges for financialTable and spendingTable
  tableIds.forEach((id) => {
    const tableRegex = new RegExp(`<table[^>]*id="${id}"[\\s\\S]*?<\\/table>`, 'gi');
    let match;
    while ((match = tableRegex.exec(htmlContent)) !== null) {
      tableStartIndexes.push(match.index);
      tableEndIndexes.push(match.index + match[0].length);
    }
  });

  // Check if a given index is within any table or footer
  const isInAnyTableOrFooter = (index) =>
    tableStartIndexes.some((start, i) => index >= start && index <= tableEndIndexes[i]) ||
    (footerStartIndex !== -1 && index >= footerStartIndex && index <= footerEndIndex);

  // Replace smartfields in the HTML content
  const updatedText = htmlContent.replace(
    /<span\s+[^>]*data-smartfield="\[([a-zA-Z0-9_.-]+)\/\]"[^>]*>(.*?)<\/span>/g,
    (match, key, originalText, offset) => {
      const value = values[key] || `[${key}/]`;

      if (isInAnyTableOrFooter(offset)) {
        return `<span data-smartfield="[${key}/]" contenteditable="false" style="color: #000;">[${key}/]</span>`;
      }

      return `<span data-smartfield="[${key}/]" contenteditable="false" style="color: #1472ff;">${value}</span>`;
    }
  );

  return updatedText;
}

function formatDate(dateString) {
  if (!dateString) return '';
  try {
    const date = parseISO(dateString);
    return format(date, 'dd/MM/yyyy', { locale: fr });
  } catch (error) {
    console.error('Erreur lors du formatage de la date:', error);
    return dateString;
  }
}

const InformationGenerate = ({ opcoRecord, refetch, toGenerate = false }) => {
  const { classes, css, cx, global } = useStyles(styles);
  const { addToast } = useToast();
  const { idUser } = useAuth();

  const { data: infos = {} } = useQuery(getProfilePresentation, {
    key: ['CFAImageProfile', idUser],
    variables: { id: idUser },
  });

  const { data: convention = {} } = useQuery(getConventionByRecord, {
    key: ['convention', opcoRecord?.id],
    variables: { id: opcoRecord?.id },
  });


  const {
    error: errorGenerateContract,
    isLoading: isLoadingGenerateContract,
    isSuccess: isSuccessGenerateContract,
    mutateAsync: mutateGenerateContract,
  } = useMutation(generateContract);

  const {
    error: errorDownloadCv,
    isLoading: isLoadingDownloadCv,
    isSuccess: isSuccessDownloadCover,
    data: dataObject,
    mutateAsync: mutateDownloadCv,
  } = useMutation(downloadCv);

  const conventionFormatted = transformDocument(convention);

  useEffect(() => {
    if (isSuccessGenerateContract) {
      addToast({
        status: 'success',
        message: 'Le contrat et la convention ont été générés et envoyés avec succès',
      });
      refetch();
    } else if (errorGenerateContract) {
      addToast({
        status: 'error',
        message: `Une erreur est survenue lors de la génération du contrat : ${errorGenerateContract?.response?.data?.description || ''}`,
      });
    }
  }, [errorGenerateContract, isSuccessGenerateContract]);

  const handleGenerateContract = async () => {
    const values = {
      // CFA
      cfaName: opcoRecord?.training?.cfa?.name || '',
      cfaAddress: opcoRecord?.training?.cfa?.address?.label || '',
      cfaSiret: opcoRecord?.training?.cfa?.siret || '',
      cfaUai: opcoRecord?.training?.cfa?.uai || '',

      // Entreprise
      businessName: opcoRecord?.businessEmployer?.business?.name || '',
      businessAddress: opcoRecord?.businessEmployer?.business?.address?.label || '',
      businessSiret: opcoRecord?.businessEmployer?.business?.siret || '',
      employer: `${opcoRecord?.businessEmployer?.firstname || ''} ${opcoRecord?.businessEmployer?.lastname || ''}`.trim(),
      idcc: opcoRecord?.businessEmployer?.codeIDCC || '',

      // Formation
      formationName: opcoRecord?.training?.label || '',
      rncpCode: opcoRecord?.training?.rncp || '',
      diplomaCode: opcoRecord?.training?.degree?.id || '',
      formationStartDate: formatDate(opcoRecord?.training?.openingDate),
      formationEndDate: formatDate(opcoRecord?.training?.closingDate),
      formationHours: `${opcoRecord?.training?.duration || ''} heures`,
      formationLocation: opcoRecord?.training?.address?.label || '',

      // Maître d'apprentissage
      apprenticeMaster: `${opcoRecord?.businessContract?.businessContractMasters?.[0]?.firstname || ''} ${opcoRecord?.businessContract?.businessContractMasters?.[0]?.lastname || ''}`.trim(),
      apprenticeMasterFunction: opcoRecord?.businessContract?.businessContractMasters?.[0]?.occupiedPosition || '',
      apprenticeMasterEmail: opcoRecord?.businessContract?.businessContractMasters?.[0]?.email || '',

      // Apprenti
      apprentice: `${opcoRecord?.individual?.firstname || ''} ${opcoRecord?.individual?.lastname || ''}`.trim(),
      contractStartDate: formatDate(opcoRecord?.businessContract?.effectiveDate),
      contractEndDate: formatDate(opcoRecord?.businessContract?.endDate),
    };

    // Create a React element to render
    const contentElement = (
      <div className="page-content">
        <Header profilImageUrl={infos?.profilImageUrl} type="convention" />
        <div dangerouslySetInnerHTML={{ __html: conventionFormatted?.text }} />
        <Footer />
      </div>
    );

    const contentHTML = renderToString(contentElement);

    const conventionWithSmartfields = fillSmartfields(contentHTML, values);

    const html = generateCompleteHTML(conventionWithSmartfields);

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const values2 = {
      spendingTable: !!doc.getElementById('spendingTable'),
      financialTable: !!doc.getElementById('financialTable'),
    }

    // Appelle l'API pour générer le PDF (et recevoir le Blob)
    const response = await mutateDownloadCv({
      data: { html },
      endpoint: '/cfa/generate-convention-pdf'
    });

    const pdfBlob = response.data;

    // Préparer les données pour l'envoi
    const formData = new FormData();
    formData.append('file', pdfBlob, `${conventionFormatted?.name}.pdf`);
    formData.append('data', new Blob([JSON.stringify(values2)], { type: 'application/json' }));

    console.log(formData.get('data'));

    // Envoyer le contrat
    mutateGenerateContract({ id: opcoRecord?.id, formData });
  };

  useEffect(() => {
    if (toGenerate) {
      handleGenerateContract();
    }
  }, [toGenerate]);

  return (
    <>
      {!isSuccessGenerateContract && (
        <div
          className={cx(global.fcjc, classes.information, { [classes.disabled]: isLoadingGenerateContract || isLoadingDownloadCv })}
          onClick={() => !isLoadingGenerateContract && !isLoadingDownloadCv && handleGenerateContract()}
          aria-disabled={isLoadingGenerateContract || isLoadingDownloadCv}
        >
          <div className={global.frac} style={{ gap: 8 }}>
            <div className={classes.contractSpan}>DONNÉES COMPLÉTÉES</div>
            <h1 className={global.title} style={{ margin: 0 }}>Envoyer la convention et le CERFA</h1>
            {isLoadingGenerateContract || isLoadingDownloadCv ? (
              <CircularProgress size={12} style={{ color: '#0060F9' }} />
            ) : (
              <ArrowForwardRounded style={{ color: '#0060F9', fontSize: 12 }} />
            )}
          </div>
          <div className={classes.kawaiiText}>
            Cliquez pour prévisualiser et envoyer automatiquement la convention et le CERFA
          </div>
        </div>
      )}
    </>
  );
};

const styles = ({ fontSizes, spacing }) => ({
  information: {
    backgroundColor: "#C0D9FF",
    cursor: "pointer",
    gap: 8,
    padding: 20,
    borderRadius: 10,
    "&:hover h1": {
      color: "#0060F9",
    },
  },
  disabled: {
    transition: 'all 0.3s ease',
    filter: 'grayscale(100%)',
    cursor: 'not-allowed',
    '&:hover h1': {
      color: '#152652',
    },
  },
  contractSpan: {
    backgroundColor: "#0060F9",
    borderRadius: '5px',
    padding: '0px 5px',
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: '12px',
    color: "white"
  },
  kawaiiText: {
    fontSize: 12,
  }
});

export default InformationGenerate;